import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../../firebase.js";
import Cookies from "js-cookie";
import { APIGetAdministratorByFirebaseToken } from "./APIs/adminApi.js";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [privilege, setPrivilege] = useState(null);
  const [privilegeSelection, setPrivilegeSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [tripTypes, setTripTypes] = useState(null);

  // Function to clear auth data (when user logs out or token refresh fails)
  const clearAuthData = () => {
    setUser(null);
    setUserId(null);
    setPrivilege(null);
    setPrivilegeSelection(null);
    setLoading(true);
    setToken(null);
    setTripTypes(null);
    Cookies.remove("email");
    Cookies.remove("institutionName");
    Cookies.remove("name");
    Cookies.remove("instCount");
    Cookies.remove("selectedInstitutionId");
    Cookies.remove("timezone");
    signOut(auth);
  };

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!isMounted) return;

      if (currentUser) {
        try {
          // Get fresh Firebase token
          const token = await currentUser.getIdToken(true);
          setToken(token);

          // Fetch admin details from backend
          const adminDetails = await APIGetAdministratorByFirebaseToken(
            token,
            setLoading
          );

          if (!adminDetails?.emailVerification?.isVerified) {
            console.warn("Email not verified, logging out...");

            clearAuthData();
            return;
          }

          const savedInstitutionId = Cookies.get("selectedInstitutionId");

          if (adminDetails) {
            setUser(currentUser);
            setUserId(adminDetails.userId);
            setPrivilegeSelection(adminDetails.privileges);

            const matchedPrivilege = adminDetails.privileges.find(
              (p) => p.institutionId === savedInstitutionId
            );

            if (matchedPrivilege?.modules) {
              for (const key in matchedPrivilege.modules) {
                const val = matchedPrivilege.modules[key];
                matchedPrivilege.modules[key] =
                  typeof val === "string"
                    ? val.toUpperCase() === "TRUE"
                    : !!val;
              }
            }

            if (adminDetails.privileges[0]?.modules) {
              for (const key in adminDetails.privileges[0].modules) {
                const val = adminDetails.privileges[0].modules[key];
                adminDetails.privileges[0].modules[key] =
                  typeof val === "string"
                    ? val.toUpperCase() === "TRUE"
                    : !!val;
              }
            }
            const selectedPrivilege =
              matchedPrivilege || adminDetails.privileges[0];

            setPrivilege(selectedPrivilege);
            setTripTypes(selectedPrivilege.tripTypes);

            // Store additional details in cookies
            Cookies.set("email", adminDetails.email);
            Cookies.set(
              "name",
              `${adminDetails.firstName} ${adminDetails.lastName}`
            );
            Cookies.set("instCount", adminDetails.privileges.length);
            window.dispatchEvent(new Event("cookieUpdate"));

            // Optionally, update the institution name cookie if needed
            if (matchedPrivilege?.institutionName) {
              Cookies.set("institutionName", matchedPrivilege.institutionName);
            } else if (adminDetails.privileges[0]?.institutionName) {
              Cookies.set(
                "institutionName",
                adminDetails.privileges[0].institutionName
              );
            }
          }
        } catch (error) {
          console.error("Error fetching admin data:", error);
          clearAuthData(); // Clear state if the token fetch fails
        }
      } else {
        clearAuthData(); // User is logged out
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Periodically refresh token to prevent unexpected logout
  useEffect(() => {
    const refreshTokenInterval = setInterval(async () => {
      if (user) {
        try {
          const newToken = await user.getIdToken(true);
          setToken(newToken);
        } catch (error) {
          clearAuthData(); // If refresh fails, log the user out
        }
      }
    }, 30 * 60 * 1000); // Refresh every 30 minutes

    return () => clearInterval(refreshTokenInterval);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userId,
        privilege,
        privilegeSelection,
        token,
        loading,
        tripTypes,
        setPrivilege,
        setPrivilegeSelection,
        setTripTypes
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
